import React, { useMemo, useState } from 'react';
import { PropertyAmenityFormRow, PROPERTY_AMENITY_LIMITS } from './types';
import { CategoryDropdown, useCategoriesByType } from '../category';
import { CurrencyInputField, DatePicker } from '../../../components/common';
import {
  calculateRemainingValue,
  getVisiblePropertyAmenities,
  todayDisplayDate
} from './utils/propertyAmenityDepreciation';
import { renderWrappedText } from './utils/renderWrappedText';
import '../../../components/common/CRMForm.css';
import './PropertyAmenitiesSection.css';

interface PropertyAmenitiesSectionProps {
  propertiesAmenities: PropertyAmenityFormRow[];
  onChange: (propertiesAmenities: PropertyAmenityFormRow[]) => void;
}

type AmenityEditor =
  | { mode: 'add' }
  | { mode: 'edit'; index: number };

const emptyAmenityRow = (): PropertyAmenityFormRow => ({
  id: 0,
  typeId: 0,
  serviceStartDate: todayDisplayDate(),
  depreciationYears: 0,
  cost: 0,
  brand: '',
  model: '',
  description: ''
});

const formatAmount = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

const formatYears = (years: number): string => `${years} ${years === 1 ? 'yr' : 'yrs'}`;

const formatBrandModel = (amenity: PropertyAmenityFormRow): string =>
  [amenity.brand, amenity.model].map(part => (part || '').trim()).filter(Boolean).join(' ') || '—';

const PropertyAmenitiesSection: React.FC<PropertyAmenitiesSectionProps> = ({
  propertiesAmenities,
  onChange
}) => {
  const { categories: amenityTypes } = useCategoriesByType('propertyamenity');
  const [editor, setEditor] = useState<AmenityEditor | null>(null);
  const [draft, setDraft] = useState<PropertyAmenityFormRow>(emptyAmenityRow());
  const [draftError, setDraftError] = useState<string | null>(null);

  const visibleAmenities = useMemo(
    () => getVisiblePropertyAmenities(propertiesAmenities),
    [propertiesAmenities]
  );

  const resolveTypeName = (typeId: number, fallback?: string): string | undefined =>
    amenityTypes.find(c => c.id === typeId)?.name || fallback;

  const updateDraft = (changes: Partial<PropertyAmenityFormRow>) => {
    setDraft(prev => ({ ...prev, ...changes }));
    setDraftError(null);
  };

  const openAdd = () => {
    setDraft(emptyAmenityRow());
    setDraftError(null);
    setEditor({ mode: 'add' });
  };

  const openEdit = (index: number) => {
    const amenity = propertiesAmenities[index];
    if (!amenity) return;
    setDraft({ ...amenity });
    setDraftError(null);
    setEditor({ mode: 'edit', index });
  };

  const closeEditor = () => {
    setEditor(null);
    setDraft(emptyAmenityRow());
    setDraftError(null);
  };

  const validateDraft = (): boolean => {
    if (!draft.typeId) {
      setDraftError('Select an amenity type');
      return false;
    }
    if (!draft.serviceStartDate || !/^\d{2}\/\d{2}\/\d{4}$/.test(draft.serviceStartDate)) {
      setDraftError('Enter a valid service start date (mm/dd/yyyy)');
      return false;
    }
    if (!(draft.depreciationYears > 0) || draft.depreciationYears > PROPERTY_AMENITY_LIMITS.maxDepreciationYears) {
      setDraftError(`Enter depreciation greater than 0 and at most ${PROPERTY_AMENITY_LIMITS.maxDepreciationYears} years`);
      return false;
    }
    if (!draft.cost || draft.cost < 0.01) {
      setDraftError('Enter a cost of at least $0.01');
      return false;
    }
    setDraftError(null);
    return true;
  };

  const saveDraft = () => {
    if (!editor || !validateDraft()) return;

    const saved: PropertyAmenityFormRow = {
      ...draft,
      brand: draft.brand.trim(),
      model: draft.model.trim(),
      description: draft.description.trim(),
      typeName: resolveTypeName(draft.typeId, draft.typeName)
    };

    if (editor.mode === 'add') {
      onChange([...propertiesAmenities, saved]);
    } else {
      onChange(propertiesAmenities.map((row, i) =>
        i === editor.index ? { ...saved, id: row.id } : row
      ));
    }
    closeEditor();
  };

  const removeAmenity = (index: number) => {
    if (editor?.mode === 'edit' && editor.index === index) {
      closeEditor();
    }
    onChange(propertiesAmenities.filter((_, i) => i !== index));
  };

  return (
    <div className="form-section-content property-amenities-section">
      {editor && (
        <div className="form-item-group property-amenities-editor">
          <div className="app-form-group">
            <label className="app-form-label required">Amenity Type</label>
            <CategoryDropdown
              parentType="propertyamenity"
              value={draft.typeId}
              onChange={(value) => updateDraft({ typeId: value, typeName: resolveTypeName(value) })}
              required={true}
              className={draft.typeId === 0 ? 'error' : ''}
              placeholder="Select amenity type..."
            />
          </div>

          <div className="app-form-group">
            <DatePicker
              label="Service Start Date"
              required
              value={draft.serviceStartDate}
              onChange={(serviceStartDate) => updateDraft({ serviceStartDate })}
            />
          </div>

          <div className="app-form-group">
            <label className="app-form-label required" htmlFor="property-amenity-depreciation">
              Depreciation (years)
            </label>
            <input
              id="property-amenity-depreciation"
              type="number"
              className="app-form-input"
              min={0}
              max={PROPERTY_AMENITY_LIMITS.maxDepreciationYears}
              step="any"
              placeholder="e.g. 10"
              value={draft.depreciationYears || ''}
              onChange={(e) => updateDraft({ depreciationYears: Number(e.target.value) || 0 })}
            />
          </div>

          <div className="app-form-group">
            <label className="app-form-label required">Cost</label>
            <CurrencyInputField
              value={draft.cost}
              onChange={(cost) => updateDraft({ cost })}
              min={0}
              placeholder="0.00"
            />
          </div>

          <div className="app-form-group">
            <label className="app-form-label" htmlFor="property-amenity-brand">Brand</label>
            <input
              id="property-amenity-brand"
              type="text"
              className="app-form-input"
              maxLength={PROPERTY_AMENITY_LIMITS.brandMaxLength}
              placeholder="e.g. Whirlpool"
              value={draft.brand}
              onChange={(e) => updateDraft({ brand: e.target.value })}
            />
          </div>

          <div className="app-form-group">
            <label className="app-form-label" htmlFor="property-amenity-model">Model</label>
            <input
              id="property-amenity-model"
              type="text"
              className="app-form-input"
              maxLength={PROPERTY_AMENITY_LIMITS.modelMaxLength}
              placeholder="Model number"
              value={draft.model}
              onChange={(e) => updateDraft({ model: e.target.value })}
            />
          </div>

          <div className="app-form-group">
            <label className="app-form-label" htmlFor="property-amenity-description">Description</label>
            <textarea
              id="property-amenity-description"
              className="app-form-input"
              rows={3}
              maxLength={PROPERTY_AMENITY_LIMITS.descriptionMaxLength}
              placeholder="General information (max 200 characters)"
              value={draft.description}
              onChange={(e) => updateDraft({ description: e.target.value })}
            />
          </div>

          {draftError && <div className="app-form-error">{draftError}</div>}

          <div className="form-item-actions">
            <button
              type="button"
              className="app-btn app-btn-secondary"
              onClick={closeEditor}
            >
              Cancel
            </button>
            <button
              type="button"
              className="app-btn app-btn-primary"
              onClick={saveDraft}
            >
              {editor.mode === 'add' ? 'Add' : 'Update'}
            </button>
          </div>
        </div>
      )}

      {propertiesAmenities.length > 0 && (
        <div className="property-amenities-ledger" role="table" aria-label="Property amenities">
          <div className="property-amenities-ledger-header" role="row">
            <span role="columnheader">Type</span>
            <span role="columnheader">Brand / Model</span>
            <span role="columnheader">In Service</span>
            <span role="columnheader">Life</span>
            <span role="columnheader">Cost</span>
            <span role="columnheader">Remaining</span>
            <span role="columnheader">Actions</span>
          </div>
          {visibleAmenities.map(({ amenity, sourceIndex }) => (
            <div
              key={amenity.id || `amenity-temp-${sourceIndex}`}
              className={`property-amenities-ledger-row${editor?.mode === 'edit' && editor.index === sourceIndex ? ' property-amenities-ledger-row-active' : ''}`}
              role="row"
            >
              <span role="cell" className="property-amenities-ledger-type">
                {amenity.typeName || resolveTypeName(amenity.typeId) || `Type #${amenity.typeId}`}
              </span>
              <span role="cell" className="property-amenities-ledger-brand">
                {renderWrappedText(formatBrandModel(amenity), 'property-amenities-ledger-token')}
              </span>
              <span role="cell" className="property-amenities-ledger-date">{amenity.serviceStartDate || '—'}</span>
              <span role="cell" className="property-amenities-ledger-life">{formatYears(amenity.depreciationYears)}</span>
              <span role="cell" className="property-amenities-ledger-cost">{formatAmount(amenity.cost)}</span>
              <span role="cell" className="property-amenities-ledger-remaining">
                {formatAmount(calculateRemainingValue(amenity.cost, amenity.serviceStartDate, amenity.depreciationYears))}
              </span>
              <span role="cell" className="property-amenities-ledger-actions">
                <button
                  type="button"
                  className="property-amenities-btn-edit"
                  onClick={() => openEdit(sourceIndex)}
                  aria-label="Edit amenity"
                  disabled={editor !== null}
                >
                  ✎
                </button>
                <button
                  type="button"
                  className="app-btn-delete"
                  onClick={() => removeAmenity(sourceIndex)}
                  aria-label="Delete amenity"
                  disabled={editor !== null}
                />
              </span>
            </div>
          ))}
        </div>
      )}

      {!editor && (
        <button
          type="button"
          className="app-btn app-btn-secondary"
          onClick={openAdd}
        >
          Add Amenity
        </button>
      )}
    </div>
  );
};

export default PropertyAmenitiesSection;
